/**
 * @fileoverview Expandable User Detail Table
 * @description Shows per-user cost/model breakdown. Click a user row to expand
 *              and see which models they used, cost per model, and request counts.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getUserLabel, formatModelName } from '../utils';
import './UserDetailTable.css';

const CHART_COLORS = [
  '#01426A', '#0097A9', '#00AF66', '#FFB81C', '#E31837',
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6',
];

const FEATURE_LABELS = {
  chat_panel_agent_mode: 'Chat / Agent',
  chat_panel: 'Chat',
  code_completion: 'Code Completion',
  code_review: 'Code Review',
  pull_request_summary: 'PR Summary',
  unknown: 'Other',
};

function formatFeature(feat) {
  return FEATURE_LABELS[feat] || feat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCost(v) {
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(3)}`;
}

const UserDetailTable = ({ users = [] }) => {
  const [expandedUser, setExpandedUser] = useState(null);

  const usersWithTokens = users.filter(
    (u) => u.token_usage?.premium_requests?.total > 0
  );

  if (!usersWithTokens.length) {
    return (
      <div className="udt-empty">No per-user model breakdown data available.</div>
    );
  }

  const toggleUser = (username) => {
    setExpandedUser((prev) => (prev === username ? null : username));
  };

  return (
    <div className="udt-root">
      <div className="udt-header-row">
        <div className="udt-col-expand" />
        <div className="udt-col-user">User</div>
        <div className="udt-col-requests">Premium Requests</div>
        <div className="udt-col-cost">Est. Cost</div>
      </div>

      {usersWithTokens
        .sort((a, b) => (b.token_usage?.estimated_cost_usd || 0) - (a.token_usage?.estimated_cost_usd || 0))
        .map((u) => {
          const username = u.username;
          const label = getUserLabel(u);
          const isExpanded = expandedUser === username;
          const totalReqs = u.token_usage?.premium_requests?.total || 0;
          const totalCost = u.token_usage?.estimated_cost_usd || 0;
          const modelsDetail = u.token_usage?.models_detail || [];

          return (
            <div key={username} className="udt-user-block">
              <div
                role="button"
                tabIndex={0}
                className={`udt-user-row ${isExpanded ? 'udt-user-row--expanded' : ''}`}
                onClick={() => toggleUser(username)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleUser(username); } }}
                aria-expanded={isExpanded}
              >
                <div className="udt-col-expand">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
                <div className="udt-col-user">
                  <span className="udt-display-name">{label}</span>
                  {label !== username && (
                    <span className="udt-github-handle">@{username}</span>
                  )}
                </div>
                <div className="udt-col-requests">{totalReqs.toLocaleString()}</div>
                <div className="udt-col-cost">{formatCost(totalCost)}</div>
              </div>

              {isExpanded && (
                <div className="udt-detail-panel">
                  <div className="udt-top-models-header">Top Models</div>
                  <div className="udt-top-models-sub">Highest-spend models for this user</div>
                  <div className="udt-models-list">
                    {modelsDetail.map((m, idx) => {
                      const pct = totalCost > 0 ? ((m.estimated_cost_usd / totalCost) * 100).toFixed(0) : 0;
                      const color = CHART_COLORS[idx % CHART_COLORS.length];
                      return (
                        <div key={m.model} className="udt-model-item">
                          <div className="udt-model-item-left">
                            <div className="udt-model-item-name">
                              <span className="udt-model-dot" style={{ background: color }} />
                              {formatModelName(m.model)}
                            </div>
                            <div className="udt-model-item-feature">{formatFeature(m.feature)}</div>
                          </div>
                          <div className="udt-model-item-right">
                            <div className="udt-model-item-cost-row">
                              <span className="udt-model-item-cost">{formatCost(m.estimated_cost_usd)}</span>
                              <span className="udt-model-item-pct">&middot; {pct}%</span>
                            </div>
                            <div className="udt-model-item-meta">
                              {m.requests.toLocaleString()} requests
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {u.token_usage?.cli?.total_tokens > 0 && (
                    <div className="udt-cli-info">
                      <strong>CLI Usage:</strong>{' '}
                      {u.token_usage.cli.requests} requests &middot;{' '}
                      {u.token_usage.cli.prompt_tokens.toLocaleString()} prompt tokens &middot;{' '}
                      {u.token_usage.cli.output_tokens.toLocaleString()} output tokens
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default UserDetailTable;
